import type { PromotionMetricsTrendPoint } from '@/client/types/promotion';

export type RangePreset = 'month' | 'year' | 'all' | 'custom';

/** Earliest year the toolbar lets you page back to. */
export const MIN_YEAR = 2023;

const ghs0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'GHS', maximumFractionDigits: 0 });
const ghsCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'GHS',
  notation: 'compact',
  maximumFractionDigits: 1,
});
export const money = (n: number) => ghs0.format(n);
export const moneyCompact = (n: number) => ghsCompact.format(n);
export const numberFmt = new Intl.NumberFormat('en-US');

export const parseDay = (s: string) => new Date(`${s}T00:00:00`);
export const isoDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// ── Colors ───────────────────────────────────────────────────────────────────

const PALETTE = [
  '#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6',
  '#14b8a6', '#ef4444', '#84cc16', '#f97316', '#06b6d4', '#a855f7',
];

export type ColorScale = (key: string) => string;

/**
 * Tier and category labels are whatever the API returns — every pricing plan
 * that doesn't canonicalise to one of the four standard tiers arrives verbatim,
 * so a dozen-plus keys is normal. Colours are handed out in the order the keys
 * are seeded (revenue desc) rather than hashed, because hashing collided often
 * enough to put two identical segments side by side in a stacked bar. Keys that
 * show up later — e.g. a refunded-only category, which never reaches the paid
 * aggregates — get appended on first use.
 */
export function makeColorScale(keys: string[] = []): ColorScale {
  const map = new Map<string, string>();
  let next = 0;
  const assign = (key: string) => {
    const color = PALETTE[next % PALETTE.length];
    next += 1;
    map.set(key, color);
    return color;
  };
  keys.forEach(assign);
  return (key) => map.get(key) ?? assign(key);
}

// ── Range presets ────────────────────────────────────────────────────────────

export interface ResolvedRange {
  /** Inclusive `YYYY-MM-DD` bounds, sent to the API as `from`/`to`. */
  from: string;
  to: string;
  label: string;
}

const fmtDay = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * The API derives the bucket size from the span (≤62 days → day, ≤750 → month,
 * else year), which already matches what each preset wants — so no `bin` is sent.
 */
export function resolveRange(
  preset: RangePreset,
  opts: { year: number; custom: { start: string; end: string }; today?: Date },
): ResolvedRange {
  const today = opts.today ?? new Date();
  switch (preset) {
    case 'month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        from: isoDay(start),
        to: isoDay(today),
        label: today.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
      };
    }
    case 'year': {
      const start = new Date(opts.year, 0, 1);
      const end = opts.year === today.getFullYear() ? today : new Date(opts.year, 11, 31);
      return { from: isoDay(start), to: isoDay(end), label: String(opts.year) };
    }
    case 'all': {
      const start = new Date(MIN_YEAR, 0, 1);
      return { from: isoDay(start), to: isoDay(today), label: `${MIN_YEAR} – ${today.getFullYear()}` };
    }
    case 'custom':
    default: {
      const s = opts.custom.start ? parseDay(opts.custom.start) : new Date(today.getFullYear(), today.getMonth(), 1);
      const e = opts.custom.end ? parseDay(opts.custom.end) : today;
      const [lo, hi] = s <= e ? [s, e] : [e, s];
      return { from: isoDay(lo), to: isoDay(hi), label: `${fmtDay(lo)} – ${fmtDay(hi)}` };
    }
  }
}

// ── Trend shaping ────────────────────────────────────────────────────────────

export type TrendRow = { label: string; total: number } & Record<string, number | string>;

export interface TrendSeries {
  rows: TrendRow[];
  /** Tier keys that actually carry revenue, highest earner first. */
  tiers: string[];
}

/**
 * Recharts wants one flat object per bucket and an explicit series list. Tiers
 * with no revenue anywhere in the window are dropped so the legend stays honest.
 */
export function toTrendSeries(trend: PromotionMetricsTrendPoint[]): TrendSeries {
  const totals = new Map<string, number>();
  trend.forEach((point) => {
    Object.entries(point.by_tier ?? {}).forEach(([tier, amount]) => {
      totals.set(tier, (totals.get(tier) ?? 0) + (amount ?? 0));
    });
  });

  const tiers = [...totals.entries()]
    .filter(([, amount]) => amount > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([tier]) => tier);

  const rows = trend.map((point) => {
    const row = { label: point.label, total: point.total } as TrendRow;
    tiers.forEach((tier) => {
      row[tier] = point.by_tier?.[tier] ?? 0;
    });
    return row;
  });

  return { rows, tiers };
}
